import { useCallback, useEffect, useRef, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useVault } from '@/hooks/useVault';
import {
  publishWorkspaceSync,
  pullWorkspaceSync,
  type SyncStatus,
} from '@/lib/workspace/sync';
import { useWorkspace } from '@/hooks/useWorkspace';
import { buildWorkspaceExport } from '@/lib/workspace/export';
import {
  estimateExportStats,
  formatBytes,
} from '@/lib/workspace/validate';
import type { WorkspaceSettings } from '@/lib/workspace/types';

export interface SyncPreviewStats {
  projects: number;
  chats: number;
  messages: number;
  providers: number;
  approxBytes: number;
  warnSize: boolean;
  warnMessages: boolean;
  excludeMessages: boolean;
}

export interface PullResult {
  imported: boolean;
  createdAt?: number;
  stats?: {
    projects: number;
    chats: number;
    messages: number;
    approxBytes?: number;
  };
}

export interface UseWorkspaceSyncOptions {
  /**
   * When true, may show a one-time in-UI auto-pull offer on empty devices.
   * Only enable in ONE mount (Settings panel) — never in sidebar chips.
   */
  enableAutoPull?: boolean;
}

function autoPullKey(pubkey: string): string {
  return `${window.location.origin}::${pubkey}`;
}

const autoPullHandledKeys = new Set<string>();
let autoPullOfferClaimed = false;

/**
 * Encrypted workspace sync over Nostr (kind 39987).
 *
 * Timestamps live in workspace settings (single source of truth) so sidebar
 * chips and Privacy panel always agree after publish/pull.
 */
export function useWorkspaceSync(options: UseWorkspaceSyncOptions = {}) {
  const { enableAutoPull = false } = options;
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { unlocked } = useVault();
  const { refresh, settings, projects, chats, ready, updateSettings } = useWorkspace();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | undefined>();
  const [autoPullOffer, setAutoPullOffer] = useState<string | null>(null);
  const offerKeyRef = useRef<string | null>(null);

  // Single source of truth: workspace settings (updated via updateSettings)
  const status: SyncStatus = {
    lastPublishedAt: settings.syncLastPublishedAt,
    lastPulledAt: settings.syncLastPulledAt,
    lastError,
  };

  const persistSyncMeta = useCallback(
    async (patch: Partial<WorkspaceSettings>) => {
      await updateSettings(patch);
    },
    [updateSettings],
  );

  const getPublishPreview = useCallback(async (): Promise<SyncPreviewStats> => {
    const data = await buildWorkspaceExport();
    const stats = estimateExportStats(data);
    return {
      ...stats,
      excludeMessages: !!(settings.syncExcludeMessages || data.messages.length === 0),
    };
  }, [settings.syncExcludeMessages]);

  const publish = useCallback(async () => {
    if (!user?.signer) throw new Error('Log in to sync');
    if (!unlocked) {
      throw new Error('Unlock local encryption before publish');
    }
    setBusy(true);
    setError(null);
    setLastError(undefined);
    try {
      const result = await publishWorkspaceSync({
        signer: user.signer as Parameters<typeof publishWorkspaceSync>[0]['signer'],
        pubkey: user.pubkey,
        publish: async (event) => {
          await nostr.event(event);
        },
      });
      // Prefer wall-clock so UI shows "just now" even if event created_at is coarse
      const at = Math.max(result.createdAt, Date.now());
      await persistSyncMeta({ syncLastPublishedAt: at });
      return { ...result, createdAt: at };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Publish failed';
      setError(msg);
      setLastError(msg);
      throw err;
    } finally {
      setBusy(false);
    }
  }, [user, nostr, persistSyncMeta, unlocked]);

  const pull = useCallback(
    async (mode: 'merge' | 'replace' = 'replace'): Promise<PullResult> => {
      if (!user?.signer) throw new Error('Log in to sync');
      if (!unlocked) {
        throw new Error('Unlock local encryption before pull');
      }
      setBusy(true);
      setError(null);
      setLastError(undefined);
      try {
        const result = await pullWorkspaceSync({
          signer: user.signer as Parameters<typeof pullWorkspaceSync>[0]['signer'],
          pubkey: user.pubkey,
          mode,
          query: async (filters) => {
            const controller = new AbortController();
            const timer = window.setTimeout(() => controller.abort(), 12_000);
            try {
              return await nostr.query(filters, { signal: controller.signal });
            } finally {
              window.clearTimeout(timer);
            }
          },
        });
        if (result.imported) {
          const at = Math.max(result.createdAt ?? 0, Date.now());
          const key = user ? autoPullKey(user.pubkey) : undefined;
          if (key) autoPullHandledKeys.add(key);
          await persistSyncMeta({
            syncLastPulledAt: at,
            historyMode: 'sync',
            ...(key ? { syncAutoPullDoneKey: key } : {}),
          });
          await refresh();
          window.dispatchEvent(new Event('opendork:providers-restored'));
          setAutoPullOffer(null);
          offerKeyRef.current = null;
          autoPullOfferClaimed = false;
          return {
            imported: true,
            createdAt: at,
            stats: result.stats,
          };
        }
        return {
          imported: false,
          createdAt: result.createdAt,
          stats: result.stats,
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Pull failed';
        setError(msg);
        setLastError(msg);
        throw err;
      } finally {
        setBusy(false);
      }
    },
    [user, nostr, persistSyncMeta, refresh, unlocked],
  );

  const dismissAutoPullOffer = useCallback(async () => {
    const key = offerKeyRef.current ?? autoPullOffer;
    setAutoPullOffer(null);
    offerKeyRef.current = null;
    autoPullOfferClaimed = false;
    if (key) {
      autoPullHandledKeys.add(key);
      try {
        await persistSyncMeta({ syncAutoPullDoneKey: key });
      } catch {
        // ignore
      }
    }
  }, [autoPullOffer, persistSyncMeta]);

  const acceptAutoPullOffer = useCallback(async () => {
    const key = offerKeyRef.current ?? autoPullOffer;
    setAutoPullOffer(null);
    autoPullOfferClaimed = false;
    try {
      const r = await pull('replace');
      if (key) {
        autoPullHandledKeys.add(key);
        if (!r.imported) {
          await persistSyncMeta({ syncAutoPullDoneKey: key });
        }
      }
      return r;
    } catch (err) {
      if (key) {
        autoPullHandledKeys.add(key);
        try {
          await persistSyncMeta({ syncAutoPullDoneKey: key });
        } catch {
          // ignore
        }
      }
      throw err;
    } finally {
      offerKeyRef.current = null;
    }
  }, [pull, persistSyncMeta, autoPullOffer]);

  useEffect(() => {
    if (!enableAutoPull) return;
    if (!ready || !user || !unlocked) return;
    if (settings.historyMode !== 'sync') return;

    const key = autoPullKey(user.pubkey);
    if (autoPullHandledKeys.has(key)) return;
    if (settings.syncAutoPullDoneKey === key) {
      autoPullHandledKeys.add(key);
      return;
    }
    if (projects.length > 0) {
      autoPullHandledKeys.add(key);
      void persistSyncMeta({ syncAutoPullDoneKey: key });
      return;
    }
    if (autoPullOfferClaimed || autoPullOffer) return;

    autoPullOfferClaimed = true;
    autoPullHandledKeys.add(key);
    offerKeyRef.current = key;
    setAutoPullOffer(key);
  }, [
    enableAutoPull,
    ready,
    user,
    unlocked,
    settings.historyMode,
    settings.syncAutoPullDoneKey,
    projects.length,
    autoPullOffer,
    persistSyncMeta,
  ]);

  const localHasData = projects.length > 0 || chats.length > 0;

  return {
    busy,
    error,
    status,
    syncEnabled: settings.historyMode === 'sync',
    localHasData,
    autoPullOffer,
    acceptAutoPullOffer,
    dismissAutoPullOffer,
    publish,
    pull,
    getPublishPreview,
    formatBytes,
    clearError: () => {
      setError(null);
      setLastError(undefined);
    },
  };
}
